// Past the header's scroll-driven animations (header-sticky.css), which end by 32px -- SG 20261027
const SETTLE_FLOOR = 200;
// Safari's scrolling thread can leave a threaded scroll-driven animation on a stale frame until the
// page is next drawn from scratch (switching away and back clears it). Once scrolling stops, the
// header's and filter bar's animations pause for a frame, which hands each back to its correct
// value and sends it again (header-sticky.css). -- SG 20261027
const RESYNC_DELAY = 150;
const RESYNC_CLASS = "scroll-anim-resync";
const SCROLL_DRIVEN =
    typeof CSS !== "undefined" && CSS.supports("animation-timeline: scroll()") && CSS.supports("timeline-scope: --a");

class Announcement extends HTMLElement {
    private lastScrollTop: number;
    private headerEle: Element | null | undefined;
    private linkRotateInterval: ReturnType<typeof setInterval> | null = null;
    private resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private resyncTimer: ReturnType<typeof setTimeout> | null = null;
    private abortController = new AbortController();
    private readonly ROTATE_INTERVAL = 3000;
    private readonly FADE_DURATION = 400;
    private readonly MEDIUM_BREAKPOINT = 1200;
    private readonly RESIZE_DEBOUNCE = 150;

    constructor() {
        super();
        this.lastScrollTop = 0;
        this.headerEle = document.querySelector(".section-header");
        this.bindEvents();
    }

    disconnectedCallback() {
        this.abortController.abort();
        if (this.linkRotateInterval) { clearInterval(this.linkRotateInterval); this.linkRotateInterval = null; }
        if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
        if (this.resyncTimer) clearTimeout(this.resyncTimer);
    }

    bindEvents() {
        const opts = { signal: this.abortController.signal };
        this.announcementToggle();
        window.addEventListener("scroll", this.announcementToggle.bind(this), { passive: true, ...opts });
        if (SCROLL_DRIVEN) window.addEventListener("scroll", this.scheduleResync.bind(this), { passive: true, ...opts });
        this.initLinkRotation();
        window.addEventListener("resize", this.handleResize.bind(this), opts);
    }

    private scheduleResync() {
        if (this.resyncTimer) clearTimeout(this.resyncTimer);
        this.resyncTimer = setTimeout(() => {
            this.resyncTimer = null;
            const targets = [this.headerEle, document.querySelector(".collection__facets-bar")].filter(
                (el): el is Element => !!el,
            );
            targets.forEach((el) => el.classList.add(RESYNC_CLASS));
            requestAnimationFrame(() => targets.forEach((el) => el.classList.remove(RESYNC_CLASS)));
        }, RESYNC_DELAY);
    }

    handleResize() {
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = setTimeout(() => {
            this.initLinkRotation();
        }, this.RESIZE_DEBOUNCE);
    }

    initLinkRotation() {
        const links = this.querySelectorAll<HTMLElement>(".header-announcement__link");
        if (links.length < 2) return;

        if (this.linkRotateInterval) {
            clearInterval(this.linkRotateInterval);
            this.linkRotateInterval = null;
        }

        const isMedium = window.innerWidth >= 991 && window.innerWidth < this.MEDIUM_BREAKPOINT;

        const rightContainer = this.querySelector<HTMLElement>(".header-announcement__right");
        if (!rightContainer) return;

        if (isMedium) {
            // Temporarily show all links to measure widths
            links.forEach((link) => {
                link.removeAttribute("data-hidden");
                link.removeAttribute("data-fading");
            });

            // Find the widest link and lock the container width
            let maxWidth = 0;
            links.forEach((link) => {
                maxWidth = Math.max(maxWidth, link.offsetWidth);
            });
            rightContainer.style.width = `${maxWidth}px`;
            rightContainer.setAttribute("data-rotating", "");

            // Now hide all but the first
            links.forEach((link, i) => {
                if (i !== 0) {
                    link.setAttribute("data-hidden", "");
                }
            });

            let activeIndex = 0;
            this.linkRotateInterval = setInterval(() => {
                const current = links[activeIndex];
                const nextIndex = (activeIndex + 1) % links.length;
                const next = links[nextIndex];

                current.setAttribute("data-fading", "");
                setTimeout(() => {
                    current.setAttribute("data-hidden", "");
                    current.removeAttribute("data-fading");
                    next.removeAttribute("data-hidden");
                    next.removeAttribute("data-fading");
                }, this.FADE_DURATION);

                activeIndex = nextIndex;
            }, this.ROTATE_INTERVAL);
        } else {
            rightContainer.style.width = "";
            rightContainer.removeAttribute("data-rotating");
            links.forEach((link) => {
                link.removeAttribute("data-hidden");
                link.removeAttribute("data-fading");
            });
        }
    }

    announcementToggle(event?: Event) {
        const countrySelector = this.querySelector(".country-selector");
        let scrollTop = window.scrollY;
        // Read before any class changes below, so layout is still clean. Not cached: the page's
        // height can change without the body's box resizing, which left the settle stuck off. -- SG 20261027
        const pageHeight = document.documentElement.scrollHeight;
        // iOS reports positions past the page's end while it bounces there. Springing back read as
        // scrolling up, which brought the announcement bar back and moved the header mid-bounce. -- SG 20261027
        scrollTop = Math.min(scrollTop, Math.max(0, pageHeight - window.innerHeight));
        const megaPanelOpen = this.headerEle?.classList.contains("desktop-mega-panel-open");

        if (scrollTop <= 0) {
            if (!megaPanelOpen) {
                this.classList.remove("hide");
                this.headerEle?.classList.add("with-announcement-bar");
            }
        }
        else if (scrollTop > this.lastScrollTop) {
            this.classList.add("hide");
            this.headerEle?.classList.remove("with-announcement-bar");
            countrySelector?.setAttribute("hidden", "true");
        }
        else if (scrollTop < this.lastScrollTop) {
            if (!megaPanelOpen) {
                this.classList.remove("hide");
                this.headerEle?.classList.add("with-announcement-bar");
            }
        }

        // The floating outline and shadow are painted by header-sticky.css -- SG 20261026
        if (scrollTop <= 0) {
            this.headerEle?.classList.remove("sticky-header");
        }
        else {
            this.headerEle?.classList.add("sticky-header");
        }

        // Within a screen of the bottom, the header's scroll-driven animations come off and the
        // class drivers hold the floating look: iOS loses the header's content while the page
        // rubber-bands past its end with them attached. -- SG 20261027
        const settled = scrollTop > SETTLE_FLOOR && scrollTop + 2 * window.innerHeight > pageHeight;
        this.headerEle?.classList.toggle("header-settled", settled);

        this.lastScrollTop = scrollTop;
    }
}

customElements.define("header-announcement", Announcement);